// Import Libraries
import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Link as MuiLink } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// Import Components
import GeneralModal from '../../components/Modals/GeneralModal.js';
import SceneTable from './sceneTable';
import ShotTable from './shotTable';
import ShotListTable from './ShotListTable';
import ShotTableHeader from '../../components/Fragments/ShotTableHeader';
import ProjectHeader from '../../components/Fragments/ProjectHeader/ProjectHeader';

// Import Services
import UserService from '../../services/userService';
import ScriptService from '../../services/scriptService';
import Utils from '../../services/utilities';
import ProjectSockets from '../../services/projectSockets';
import ProjectSettings from '../../services/project/projectSettingsService';

// Context
import { UserContext } from '../../context/User';
import ScriptContext from '../../context/Script';
import SessionContext from '../../context/Session';
import ProjectViewContext from '../../context/ProjectView';

// Construct Component
const ShotList = ({ scriptId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { script, setScript } = useContext(ScriptContext);
  const { isMobile, generalModalDetails, setGeneralModalDetails } = useContext(SessionContext);
  const scriptRef = useRef(script);

  const [projectSettingsOpen, setProjectSettingsOpen] = useState(false);

  const {
    view,
    setView,
    lockedFields,
    setLockedFields,
    myLockedField,
    setMyLockedField,
    setActiveCollaborators,
    lockField,
    setLockField,
    unlockField,
    setUnlockField,
    projectExports,
    setProjectExports,
  } = useContext(ProjectViewContext);

  const [sceneClass, setSceneClass] = useState('slide-out-left');
  const [shotClass, setShotClass] = useState('slide-out-right');
  const [error, setError] = useState(false);
  const [scriptDeleted, setScriptDeleted] = useState(false);
  const componentRef = useRef(null);

  useEffect(() => {
    if (location.state && location.state.uploadedFile) {
      setView({ ...view, uploadedFile: location.state.uploadedFile });
    }
  }, [location.state]);

  useEffect(() => {
    if (isMobile) {
      if (view.onScreen === 'scene') {
        setSceneClass('offscreen-left');
        setShotClass('slide-out-right');
        setTimeout(() => {
          setSceneClass('slide-in');
        }, 100);
        setTimeout(() => {
          setShotClass('hidden');
        }, 500);
      } else if (view.onScreen === 'shot') {
        setShotClass('offscreen-right');
        setSceneClass('slide-out-left');
        setTimeout(() => {
          setShotClass('slide-in');
        }, 100);
        setTimeout(() => {
          setSceneClass('hidden');
        }, 500);
      }
    }
  }, [view.onScreen]);

  // setupPage() (below) re-fetches the whole script via REST -- once on mount, and
  // again every 4s while scene parsing is still in progress -- and previously wrote
  // the fetched `scenes` array straight over the live one. Once a scene is already
  // known client-side, its generation status (ai_status/isGeneratingShots/shot_list)
  // is only ever supposed to change via socket events (updateScene/addShot/updateShot);
  // a REST re-fetch racing one of those updates could revert an already-finished
  // scene back to "generating" in the UI. Keep the client's current copy of any scene
  // we already know about and only take the freshly-fetched copy for genuinely new
  // scenes (ones parsing has just added).
  const mergeScenes = (existingScenes, incomingScenes) => {
    const existingById = new Map((existingScenes || []).map((s) => [s.id, s]));
    return (incomingScenes || []).map((incoming) => existingById.get(incoming.id) || incoming);
  };

  const setupPageView = async (tempScript) => {
    let manualEntry = false;
    let pdfPath = null;
    if (tempScript.script_path) {
      pdfPath = await ScriptService.getPdf(tempScript.script_path, tempScript.id);
    }
    if (!pdfPath) {
      manualEntry = true;
    }
    let status = 0;
    if (script?.scenes[0]?.ai_status > 0) {
      status = script.scenes[0].ai_status;
    }
    setView((prevView) => ({
      ...prevView,
      uploadedFile: pdfPath || prevView.uploadedFile,
      manualEntry: manualEntry,
      // `script.scenes` swaps to a different version's array on a version
      // switch (mainScriptPage/ProjectViewProvider stay mounted across it --
      // see scriptView.js), so the scene index a user had scrolled to in the
      // old version must not carry over, or SceneTable/ShotTable keep
      // indexing into the new scenes with a stale position.
      sceneIndex: 0,
      viewColumns: window.innerWidth < 768 ? 1 : 2,
      isLoading: false,
      projectSettingsOpen: false,
      sceneImagesAllowed:
        tempScript?.features?.toLowerCase() === 'basic' && status === 0 ? false : true,
    }));
  };

  const setupPage = async () => {
    let pageViewSet = false;
    if (scriptId && user) {
      try {
        let tempScript = await ScriptService.getScriptById(scriptId);
        if (tempScript === false) {
          navigate('/app/home');
          return;
        }
        if (!tempScript.id) {
          // getScriptById returns {} for a 404 -- either the script was
          // deleted (soft-delete) or the id never existed. Either way the
          // URL is stale, so show a dedicated message instead of silently
          // redirecting or letting the code below throw on tempScript.scenes.
          setScriptDeleted(true);
          return;
        }
        for (let i = 0; i < tempScript.scenes.length; i++) {
          tempScript.scenes[i].shot_list = Utils.buildTempShot(tempScript, tempScript.scenes[i]);
        }
        let level = null;
        if (tempScript?.collaborators?.owner?.user_id === user.id) {
          level = 'owner';
        } else if (
          tempScript?.collaborators?.editors?.find((editor) => editor.user_id === user.id)
        ) {
          level = 'editor';
        } else if (
          tempScript?.collaborators?.viewers?.find((viewer) => viewer.user_id === user.id)
        ) {
          level = 'viewer';
        } else if (window.location.pathname.substring(0, 6) !== '/admin') {
          level = 'owner';
        } else {
          let validLocalStaffToken = await UserService.validateStaffToken();
          if (validLocalStaffToken) {
            level = 'owner';
          } else {
            navigate('/app/home');
            return;
          }
        }
        if (
          level == 'owner' &&
          tempScript.use_ai_parsing &&
          tempScript.created_at_est &&
          Date.now() - new Date(tempScript.created_at_est).getTime() <= 2 * 60 * 1000
        ) {
          handleAIparse();
        }

        let locks = await ScriptService.getActiveLocks(tempScript.id);
        setLockedFields(locks);
        tempScript.level = level;
        setScript((currentScript) => ({
          ...currentScript,
          ...tempScript,
          scenes: mergeScenes(currentScript.scenes, tempScript.scenes),
        }));

        if (tempScript.page_count !== tempScript.last_processed_page) {
          let counter = 0;
          while (counter < 20 && tempScript.page_count !== tempScript.last_processed_page) {
            if (pageViewSet === false && tempScript.scenes.length > 0) {
              pageViewSet = true;
              setupPageView(tempScript);
            }
            await new Promise((resolve) => setTimeout(resolve, 4000));
            tempScript = await ScriptService.getScriptById(scriptId);
            counter++;
            for (let i = 0; i < tempScript.scenes.length; i++) {
              tempScript.scenes[i].shot_list = Utils.buildTempShot(
                tempScript,
                tempScript.scenes[i],
              );
            }
            tempScript.level = level;
            setScript((currentScript) => ({
              ...currentScript,
              ...tempScript,
              scenes: mergeScenes(currentScript.scenes, tempScript.scenes),
            }));
          }
        } else {
          setupPageView(tempScript);
        }

        if (tempScript.scenes.length === 0 && tempScript.script_path) {
          setError(true);
        }
        return true;
      } catch (err) {
        console.error('Error setting up page:', err);
        setError(true);
      }
    }
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      ProjectSockets.disconnectSocket();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [user]);

  useEffect(() => {
    const initializeProjectPage = async () => {
      try {
        const setupSuccess = await setupPage();
        if (setupSuccess) {
          ProjectSockets.connectSocket(
            scriptId,
            user.id,
            setLockedFields,
            setView,
            setActiveCollaborators,
            scriptRef,
            setScript,
            projectExports,
            setProjectExports,
          );

          const settings = await ProjectSettings.get(scriptId);
          if (settings) {
            setScript((prevScript) => ({
              ...prevScript,
              project_settings: settings,
            }));
          }
        }
      } catch (error) {
        console.error('Error initializing script page:', error);
      }
    };

    if (scriptId && user && user.id) {
      UserService.addActionLog('View Script Shot List', scriptId, 'script');
      initializeProjectPage();
    }

    return () => {
      ProjectSockets.disconnectSocket();
    };
  }, [scriptId, user]);

  useEffect(() => {
    if (
      script &&
      script.scenes &&
      script.scenes.length > 0 &&
      script.scenes[0].id &&
      view.isLoading
    ) {
      setView((prevView) => ({
        ...prevView,
        isLoading: true,
      }));
    }
  }, [script.scenes]);

  useEffect(() => {
    scriptRef.current = script;
  }, [script]);

  // Logs a 'View Scene' action each time a different scene is opened, feeding
  // the scenes_viewed column of the staff KPI export (new_user_kpi_2026),
  // which counts DISTINCT scene ids, so repeat views are harmless. Keyed on the
  // scene id rather than view.sceneIndex so reorders / socket index shifts
  // (same scene, new index) don't log. The first scene counts on load since it
  // opens automatically -- except on mobile, where the scene list shows first
  // and a scene is only open once its shot panel slides in (onScreen 'shot').
  // Table view shows every scene at once, so it doesn't count as viewing one.
  const viewedSceneId =
    view.sceneIndex === null ? null : script.scenes?.[view.sceneIndex]?.id;
  useEffect(() => {
    if (!viewedSceneId || view.isLoading || view.tableView) return;
    if (isMobile && view.onScreen !== 'shot') return;
    UserService.addActionLog('View Scene', viewedSceneId, 'scene');
  }, [viewedSceneId, view.isLoading, view.tableView, view.onScreen, isMobile]);

  const lockFieldLogic = useCallback(
    (fieldId) => {
      if (!fieldId) return;
      const element = document.getElementById(fieldId);
      if (!element || element.disabled || element.readOnly || myLockedField === fieldId) return;

      setMyLockedField(fieldId);
      fieldId = fieldId.toString();

      let [field, subSection] = fieldId.split(':');
      if (!field) return;
      const fieldDetails = field.split('-');
      ProjectSockets.emitLockField(
        fieldDetails[0],
        fieldDetails[1],
        fieldDetails[2],
        setMyLockedField,
      );
    },
    [myLockedField],
  );

  const unlockFieldLogic = useCallback((fieldId) => {
    setMyLockedField(null);
    if (!fieldId) return;

    fieldId = fieldId.toString();
    let [field, subSection] = fieldId.split(':');
    if (!field) return;
    const fieldDetails = field.split('-');
    ProjectSockets.emitUnlockField(
      fieldDetails[0],
      fieldDetails[1],
      fieldDetails[2],
      setMyLockedField,
    );
  }, []);

  useEffect(() => {
    setLockField(() => lockFieldLogic);
    setUnlockField(() => unlockFieldLogic);
  }, [lockFieldLogic, setLockField, setUnlockField]);

  const traverseChildElements = (element) => {
    if (!element) return;

    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)) {
      const handleFocus = (event) => {
        const fieldId = event.target.getAttribute('name') || event.target.getAttribute('id');
        lockFieldLogic(fieldId);
      };

      const handleBlur = (event) => {
        const fieldId = event.target.getAttribute('name') || event.target.getAttribute('id');
        unlockFieldLogic(fieldId);
      };
      if (!element.dataset.listenersAdded) {
        element.addEventListener('focus', handleFocus);
        element.addEventListener('blur', handleBlur);
        element.dataset.listenersAdded = true;
      }
    }

    // Recursively traverse child elements
    element.childNodes.forEach((child) => traverseChildElements(child));
  };

  useEffect(() => {
    if (view.isLoading) return;
    const rootElement = componentRef.current;
    if (rootElement) {
      setTimeout(() => traverseChildElements(rootElement), 1000);
    }
  }, [
    view.sceneIndex,
    view.onScreen,
    view.isLoading,
    lockField,
    unlockField,
    view.viewColumns,
    isMobile,
  ]);

  const handleAIparse = () => {
    setGeneralModalDetails({
      open: true,
      header: <span>Script Formatting Issue</span>,
      message: (
        <div className="Padded">
          Unfortunately, this script didn't work well with our software. We did our best to parse
          it, but some scenes may be merged or misaligned. If needed, try <b>New Project</b> then
          select <b> Copy/Paste</b> to add your script one scene at a time.
          <br />
          <br />
          <div className="fullWidthContainer Padded">
            <div className="OnePortionFlex"></div>
            <button
              className="OnePortionFlex buttonClone"
              onClick={() => setGeneralModalDetails(false)}
            >
              Continue
            </button>
            <div className="OnePortionFlex"></div>
          </div>
        </div>
      ),
    });
  };

  if (scriptDeleted) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          minHeight: 'calc(100vh - 65px)',
          gap: 3,
          px: 3,
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: '#EDEBFB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DeleteOutlineIcon sx={{ color: '#7A64EB', fontSize: 32 }} />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ color: '#1B134A', fontWeight: 800, mb: 1 }}>
            Ah-oh!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
            This URL leads to a project that was deleted. Please go to{' '}
            <MuiLink
              component={RouterLink}
              to="/app/home"
              sx={{ color: '#7A64EB', fontWeight: 600 }}
            >
              My Projects
            </MuiLink>{' '}
            to open the available scripts.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <div>
      {!view.isLoading && !error && (
        <>
          {isMobile ? <div style={{ height: 64 }} /> : null} {/* spacer */}
          <div
            style={
              isMobile
                ? {
                    position: 'fixed',
                    top: 70,
                    left: 0,
                    right: 0,
                    zIndex: 1200,
                    background: '#fff',
                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                  }
                : undefined
            }
          >
            {isMobile ? <ShotTableHeader view={view} setView={setView} /> : <ProjectHeader />}
          </div>
        </>
      )}

      {generalModalDetails && (
        <GeneralModal
          generalModalDetails={generalModalDetails}
          setModalIsOpen={setGeneralModalDetails}
        />
      )}

      <div>
        {view.isLoading && !error && (
          <div className="loadingMessage" style={{ padding: '30px' }}>
            Bear with us while we organize your scenes. This could take a minute or two. We promise
            it is worth the wait!
            <br />
            <img
              src="/Spinner-2.9s-297px.gif"
              style={{ height: '200px', width: '200px' }}
              className="smallLoadingGif"
              alt="Loading.."
            />
          </div>
        )}
        {error && (
          <div className="mainBox sizeUp errorMessage">
            <h3>Well then. This is embarrassing. We don’t know what happened here.</h3>
            <br />
            Try hitting refresh on your browser, if that does not work, it is likely that we are
            unable to find scenes within the file you uploaded. Please try again and ensure the
            script meets the following critieria:
            <ul>
              <li>
                Please ensure your script has slug lines for each scene such as "INT. LOCATION -
                NIGHT"
              </li>
              <li>
                Scripts do not necessarily need to be in English each scene must begin with INT,
                EXT, OMITTED, or SCENE.{' '}
              </li>
            </ul>
            <br />
            <br />
            If your script meets this criteria, please{' '}
            <a href="https://www.shotkraft.com/help" target="_blank" rel="noreferrer">
              contact us
            </a>{' '}
            in this form so we can assist you. We are sorry for the inconvenience.
          </div>
        )}
        {!view.isLoading && !error && !view.tableView && (
          <div className="FullWidthFlex" ref={componentRef}>
            <div className={`${view.viewColumns == 2 ? 'OneHalfWidth' : 'FullWidth'} `}>
              {!isMobile ? (
                <SceneTable view={view} setView={setView} />
              ) : (
                <>
                  <div className={`table-container ${sceneClass}`}>
                    <SceneTable view={view} setView={setView} />
                  </div>
                  <div className={`table-container ${shotClass}`}>
                    <ShotTable />
                  </div>
                </>
              )}
            </div>

            {view.viewColumns == 2 && !isMobile && (
              <div className="OneHalfWidth">
                <ShotTable />
              </div>
            )}
          </div>
        )}

        {!view.isLoading && !error && view.tableView && (
          <div ref={componentRef}>
            <ShotListTable />
          </div>
        )}
      </div>
    </div>
  );
};

export default ShotList;
